import { Breadcrumb, BreadcrumbItem, BreadcrumbLink, BreadcrumbList, BreadcrumbPage, BreadcrumbSeparator } from '@/components/ui/breadcrumb';
import { Button } from '@/components/ui/button';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { Separator } from '@/components/ui/separator';
import { SidebarTrigger } from '@/components/ui/sidebar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';

import { createFileRoute, Link } from '@tanstack/react-router'
import { EllipsisVerticalIcon, CalendarIcon } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { updateAccountMutation, getAccountQuery } from '@/queries/accountMutation';
import type { AccountData } from '@/queries/accountMutation';


export const Route = createFileRoute('/_app/settings/giving-info/')({
  component: RouteComponent,
})

function RouteComponent() {
  const { data: accountData, isLoading } = useQuery(getAccountQuery());
  const updateAccount = updateAccountMutation();

  const [formData, setFormData] = useState<AccountData>({
    id: '',
    name: '',
    currentYearBegin: '',
    currentYearEnd: '',
    qbCashBasis: false,
    taxIDNumber: '',
    legalName: '',
    qbInvoiceTemplate: '',
    qbReceiptTemplate: ''
  });

  useEffect(() => {
    if (accountData) {
      setFormData(accountData);
    }
  }, [accountData]);

  const handleInputChange = (field: keyof AccountData, value: string | boolean) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleSave = async () => {
    try {
      await updateAccount.mutateAsync(formData);
      // Show success message or handle success
    } catch (error) {
      // Handle error
      console.error('Failed to update account:', error);
    }
  };

  // Format date for input (convert from ISO string to YYYY-MM-DD)
  const formatDateForInput = (dateString?: string) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toISOString().split('T')[0];
  };

  // Convert date from input format to ISO string
  const handleDateChange = (field: keyof AccountData, value: string) => {
    const isoDate = value ? new Date(value).toISOString() : '';
    handleInputChange(field, isoDate);
  };

  if (isLoading) {
    return <div>Loading...</div>;
  }

  return (
    <>
      <header
        className={`sticky top-0 flex shrink-0 items-center gap-2 p-2 bg-white border-b-4 border-l-gray-200 border-r-gray-200 border-t-gray-200 border-t-2`}
      >
        <SidebarTrigger className="-ml-1" />
        <Separator orientation="vertical" className="mr-2 h-4" />
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem className="hidden md:block">
              <BreadcrumbLink asChild>
                <Link to='/settings/org-settings'>
                  Settings
                </Link>
              </BreadcrumbLink>
            </BreadcrumbItem>

            <BreadcrumbSeparator className="hidden md:block" />
            <BreadcrumbItem>
              <BreadcrumbPage>
                Giving Info
              </BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>
        <div className="flex items-center gap-2 ml-auto mr-5">
          <Button
            onClick={handleSave}
            disabled={updateAccount.isPending}
            className="bg-green-500 hover:bg-green-600 text-white"
          >
            {updateAccount.isPending ? 'Saving...' : 'Save'}
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline">
                Actions <EllipsisVerticalIcon className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent>
              <DropdownMenuLabel>Quick Actions</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem className="text-red-500">Delete</DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </header>

      <div className="h-full w-full p-6 bg-gray-100 overflow-auto">
        <Card className="max-w-4xl">
          <CardHeader>
            <CardTitle className="text-lg font-medium">Edit Giving Info</CardTitle>
          </CardHeader>
          <CardContent className="space-y-8">
            {/* Current Year Section */}
            <div className="space-y-4">
              <h3 className="text-base font-medium border-b pb-2">Current Year</h3>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="currentYearBegin" className="text-sm font-medium">
                    Current Year Begin:
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="currentYearBegin"
                      type="date"
                      value={formatDateForInput(formData.currentYearBegin)}
                      onChange={(e) => handleDateChange('currentYearBegin', e.target.value)}
                      className="pr-10"
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>

                <div>
                  <Label htmlFor="currentYearEnd" className="text-sm font-medium">
                    Current Year End:
                  </Label>
                  <div className="relative mt-1">
                    <Input
                      id="currentYearEnd"
                      type="date"
                      value={formatDateForInput(formData.currentYearEnd)}
                      onChange={(e) => handleDateChange('currentYearEnd', e.target.value)}
                      className="pr-10"
                    />
                    <CalendarIcon className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </div>
              </div>
            </div>

            {/* Accounting Information Section */}
            <div className="space-y-4">
              <h3 className="text-base font-medium border-b pb-2">Accounting Information</h3>

              <div className="space-y-4">
                <div className="flex items-center space-x-2">
                  <Checkbox
                    id="qbCashBasis"
                    checked={formData.qbCashBasis || false}
                    onCheckedChange={(checked) => handleInputChange('qbCashBasis', checked as boolean)}
                  />
                  <Label htmlFor="qbCashBasis" className="text-sm font-medium">
                    Accounting is Cash Basis
                  </Label>
                </div>

                <div>
                  <Label htmlFor="taxIDNumber" className="text-sm font-medium">
                    Tax ID:
                  </Label>
                  <Input
                    id="taxIDNumber"
                    value={formData.taxIDNumber || ''}
                    onChange={(e) => handleInputChange('taxIDNumber', e.target.value)}
                    className="mt-1"
                    placeholder="1234"
                  />
                </div>

                <div>
                  <Label htmlFor="legalName" className="text-sm font-medium">
                    Legal Name:
                  </Label>
                  <Input
                    id="legalName"
                    value={formData.legalName || ''}
                    onChange={(e) => handleInputChange('legalName', e.target.value)}
                    className="mt-1"
                    placeholder="Legal"
                  />
                </div>
              </div>
            </div>

            {/* Quickbooks Information Section */}
            <div className="space-y-4">
              <h3 className="text-base font-medium border-b pb-2">Quickbooks Information</h3>

              <div className="space-y-4">
                <div>
                  <Label htmlFor="qbInvoiceTemplate" className="text-sm font-medium">
                    Invoice Template:
                  </Label>
                  <Input
                    id="qbInvoiceTemplate"
                    value={formData.qbInvoiceTemplate || ''}
                    onChange={(e) => handleInputChange('qbInvoiceTemplate', e.target.value)}
                    className="mt-1"
                    placeholder="Invoice Template"
                  />
                </div>

                <div>
                  <Label htmlFor="qbReceiptTemplate" className="text-sm font-medium">
                    Receipt Template:
                  </Label>
                  <Input
                    id="qbReceiptTemplate"
                    value={formData.qbReceiptTemplate || ''}
                    onChange={(e) => handleInputChange('qbReceiptTemplate', e.target.value)}
                    className="mt-1"
                    placeholder="Donation Receipt"
                  />
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    </>
  );
}
